import React, { useState, useEffect, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { XMarkIcon, PrinterIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { Button } from 'components/ui';
import { GeneratePaymentReceipt } from 'services/paymentServices';
import { toast } from 'sonner';
import { jsPDF } from 'jspdf';
import html2canvasPro from 'html2canvas-pro';
import SensationLogo from 'assets/images/Sensation-Logo.png';

export const PaymentReceiptModal = ({ isOpen, onClose, transaction, receiptType, leadId }) => {
  const [loading, setLoading] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const receiptRef = useRef(null);

  useEffect(() => {
    const fetchReceiptData = async () => {
      if (!isOpen || !transaction || !leadId) return;

      setLoading(true);
      try {
        const payload = {
          paymentLinkId: transaction.PaymentLinkId,
          leadId: leadId,
          receiptType: receiptType
        };

        const response = await GeneratePaymentReceipt(payload);
        if (response.success && response.data) {
          const data = Array.isArray(response.data) ? response.data[0] : response.data;
          setReceiptData(data);
        } else {
          toast.error(response.message || "Failed to fetch receipt details");
        }
      } catch (error) {
        console.error("Error fetching receipt data:", error);
        toast.error("An error occurred while fetching receipt details");
      } finally {
        setLoading(false);
      }
    };

    fetchReceiptData();
  }, [isOpen, transaction, receiptType, leadId]);

  const handleDownloadPDF = async () => {
    if (!receiptRef.current || isDownloading) return;

    setIsDownloading(true);
    toast.info("Generating PDF receipt...");

    try {
      const element = receiptRef.current;
      const canvas = await html2canvasPro(element, {
        scale: 2, // Crisp high-DPI quality without bloated pixel count
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: 800, // Ensure enough width for layout
        onclone: (clonedDoc) => {
          const clonedElement = clonedDoc.getElementById('payment-receipt-content');
          if (clonedElement) {
            clonedElement.style.width = '700px'; // Slightly wider for better fit
            clonedElement.style.margin = '0 auto';
            clonedElement.style.border = '1.5px solid black';
          }
        }
      });

      // Compress canvas to JPEG 0.85 (reduces file size by ~95% compared to raw PNG)
      const imgData = canvas.toDataURL('image/jpeg', 0.85);
      // Calculate dimensions to fit A4 or maintain aspect ratio
      const pdfWidth = 595.28; // A4 width in points
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'pt',
        format: 'a4',
        compress: true, // Enable stream compression
      });

      pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight, undefined, 'FAST');

      const fileName = `${receiptData?.ReceiptName || 'Payment_Receipt'}_${receiptData?.ReceiptNo || transaction.PaymentLinkId.substring(0, 8)}.pdf`;
      pdf.save(fileName);

      toast.success("Receipt downloaded successfully");
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate PDF receipt");
    } finally {
      setIsDownloading(false);
    }
  };

  if (!transaction) return null;

  const isInternal = receiptType === 1;
  const receiptTitle = receiptData?.ReceiptName || (isInternal ? 'Internal Payment Receipt' : 'Student Payment Receipt');

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[200]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 z-[50] overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all dark:bg-gray-800">
                <div className="flex items-center justify-between border-b pb-4 dark:border-gray-700">
                  <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900 dark:text-gray-100">
                    {receiptTitle}
                  </Dialog.Title>
                  <div className="flex items-center gap-3">
                    {!loading && (
                      <Button
                        variant="primary"
                        size="sm"
                        className="gap-2 !py-1.5 !px-4 shadow-sm"
                        onClick={handleDownloadPDF}
                        disabled={isDownloading}
                      >
                        {isDownloading ? (
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        ) : (
                          <ArrowDownTrayIcon className="h-4 w-4" />
                        )}
                        <span className="hidden sm:inline">{isDownloading ? 'Generating...' : 'Download PDF'}</span>
                        <span className="sm:hidden">{isDownloading ? '...' : 'PDF'}</span>
                      </Button>
                    )}
                    <button
                      onClick={onClose}
                      className="rounded-full p-1 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <XMarkIcon className="h-5 w-5 text-gray-500" />
                    </button>
                  </div>
                </div>

                {loading ? (
                  <div className="flex h-64 flex-col items-center justify-center space-y-4">
                    <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary-500 border-t-transparent"></div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Fetching receipt details...</p>
                  </div>
                ) : (
                  <div className="mt-6 space-y-6">
                    {/* Container for PDF Generation */}
                    <div
                      ref={receiptRef}
                      id="payment-receipt-content"
                      className="bg-white p-8 border-[1.5px] border-black text-gray-900 font-sans mx-auto max-w-full"
                      style={{ width: '100%', minHeight: '500px' }}
                    >
                      {/* Header Section */}
                      <div className="flex justify-between items-start mb-6">
                        <div className="flex items-start gap-4">
                          <img
                            src={SensationLogo}
                            alt="Institution Logo"
                            className="h-16 w-auto object-contain"
                          />
                        </div>
                        <div className="text-right flex flex-col items-end">
                          <div className="flex gap-4">
                            <span className="text-sm font-bold">Receipt Number</span>
                            <span className="text-sm font-bold">#{receiptData?.ReceiptNo || 'N/A'}</span>
                          </div>
                        </div>
                      </div>

                      {/* Student Details Grid */}
                      <div className="grid grid-cols-1 gap-y-2 mb-8 text-[13px]">
                        <div className="grid grid-cols-2 gap-x-8">
                          <div className="grid grid-cols-[140px_1fr] gap-2">
                            <span>Enrollment No</span>
                            <span className="font-medium">: {receiptData?.EnrollmentNo || 'N/A'}</span>
                          </div>
                          <div className="grid grid-cols-[100px_1fr] gap-2">
                            <span>Duration</span>
                            <span className="font-medium">: {receiptData?.CourseDuration || 'N/A'}</span>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-x-8">
                          <div className="grid grid-cols-[140px_1fr] gap-2">
                            <span>Received From</span>
                            <span className="font-medium">: {receiptData?.ReceivedFrom || transaction.FullName}</span>
                          </div>
                          <div className="grid grid-cols-[100px_1fr] gap-2">
                            <span>Payment Date</span>
                            <span className="font-medium">
                              : {new Date(receiptData?.PaymentDate || receiptData?.CreatedDate || transaction.PaymentDate || transaction.CreatedDate).toLocaleDateString('en-GB')}
                            </span>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-x-8">
                          <div className="grid grid-cols-[140px_1fr] gap-2">
                            <span>Phone Number</span>
                            <span className="font-medium">: {receiptData?.PhoneNumber || 'N/A'}</span>
                          </div>
                          <div className="grid grid-cols-[100px_1fr] gap-2">
                            <span>Email</span>
                            <span className="font-medium break-all">: {receiptData?.Email || 'N/A'}</span>
                          </div>
                        </div>

                        <div className="grid grid-cols-[140px_1fr] gap-2">
                          <span>Course</span>
                          <span className="font-medium truncate">: {receiptData?.Course || 'N/A'}</span>
                        </div>

                        <div className="grid grid-cols-[140px_1fr] gap-2">
                          <span>Payment Method</span>
                          <span className="font-medium">: {receiptData?.PaymentMethod || transaction.GatewayName || 'Manual'}</span>
                        </div>

                        {isInternal && (
                          <div className="grid grid-cols-[140px_1fr] gap-2">
                            <span>Paid To</span>
                            <span className="font-medium">: {receiptData?.PaidTo || 'N/A'}</span>
                          </div>
                        )}
                      </div>

                      {/* Fees Table */}
                      <div className="border border-gray-300 mb-6">
                        <table className="w-full text-left text-[13px] border-collapse">
                          <thead>
                            <tr className="bg-[#C5C5C5] border-b border-gray-300">
                              <th className="py-2 px-3 font-bold border-r border-gray-300">Fees Head</th>
                              <th className="py-2 px-3 font-bold text-right">Charges</th>
                            </tr>
                          </thead>
                          <tbody>
                            {/* <tr className="border-b border-gray-100">
                                <td className="py-2 px-3 border-r border-gray-300">FEES</td>
                                <td className="py-2 px-3 text-right">
                                  <div className="flex justify-end gap-12">
                                    <span>Rs.</span>
                                    <span className="min-w-[80px]">{Number(receiptData?.PaymentAmount || 0).toFixed(2)}</span>
                                  </div>
                                </td>
                              </tr> */}
                            <tr className="border-b border-gray-100 text-gray-500">
                              <td className="py-2 px-3 border-r border-gray-300">TOTAL AMOUNT</td>
                              <td className="py-2 px-3 text-right">
                                <div className="flex justify-end gap-12">
                                  <span>Rs.</span>
                                  <span className="min-w-[80px]">{Number(receiptData?.TotalAmount || 0).toFixed(2)}</span>
                                </div>
                              </td>
                            </tr>
                            <tr className="border-b border-gray-100 text-gray-500">
                              <td className="py-2 px-3 border-r border-gray-300">TOTAL PAID AMOUNT</td>
                              <td className="py-2 px-3 text-right">
                                <div className="flex justify-end gap-12">
                                  <span>Rs.</span>
                                  <span className="min-w-[80px]">{Number(receiptData?.TotalPaidAmount || 0).toFixed(2)}</span>
                                </div>
                              </td>
                            </tr>
                            {receiptData?.RefundedAmount > 0 && (
                              <tr className="border-b border-gray-100 text-gray-500">
                                <td className="py-2 px-3 border-r border-gray-300">TOTAL REFUNDED AMOUNT</td>
                                <td className="py-2 px-3 text-right">
                                  <div className="flex justify-end gap-12">
                                    <span>Rs.</span>
                                    <span className="min-w-[80px]">{Number(receiptData?.RefundedAmount || 0).toFixed(2)}</span>
                                  </div>
                                </td>
                              </tr>
                            )}
                            <tr className="border-b border-gray-100 text-rose-600 font-medium">
                              <td className="py-2 px-3 border-r border-gray-300">PENDING AMOUNT</td>
                              <td className="py-2 px-3 text-right">
                                <div className="flex justify-end gap-12">
                                  <span>Rs.</span>
                                  <span className="min-w-[80px]">{Number(receiptData?.PendingAmount || 0).toFixed(2)}</span>
                                </div>
                              </td>
                            </tr>
                            {/* Total Row */}
                            {/* <tr className="font-bold border-t border-gray-300">
                                <td className="py-4 px-3 border-r border-gray-300 uppercase">Paid Amount</td>
                                <td className="py-4 px-3 text-right">
                                  <div className="flex justify-end gap-12">
                                    <span>Rs.</span>
                                    <span className="min-w-[80px]">{Number(receiptData?.PaymentAmount || 0).toFixed(2)}</span>
                                  </div>
                                </td>
                              </tr> */}
                          </tbody>
                        </table>
                      </div>

                      {/* Footer Disclaimer */}
                      <div className="text-center mt-12">
                        <p className="text-[11px] text-gray-700 italic">
                          Full fee deposit is required to obtain the completion certificate
                        </p>
                      </div>
                    </div>


                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
